import { ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

/**
 * Tvåvägssynka filters <-> URL query.
 * - Läser in filter från query vid start och när URL ändras.
 * - Skriver filter till query när de ändras (behåller övriga query-keys).
 * - Undviker loopar via guard.
 *
 * Prioritet:
 * - Om URL saknar filter-nycklar låter vi befintliga filter (t.ex. från localStorage) vara kvar
 *   och synkar istället dem till URL:en.
 * - Om URL HAR någon filter-nyckel, då tar URL:en över och appliceras på filterna.
 */
export function useFilterQuerySync<
  TKeys extends readonly string[],
  TFilters extends Record<TKeys[number], string[]>
>(
  filters: Ref<TFilters>,
  filterKeys: TKeys,
  options?: {
    shouldIgnoreQuery?: (key: TKeys[number], value: string, query: Record<string, unknown>) => boolean
  }
) {
  const route = useRoute()
  const router = useRouter()
  const guard = ref(false)

  const toArray = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.map(String)
    return v != null ? [String(v)] : []
  }

  const filteredQueryValues = (key: TKeys[number], query: Record<string, unknown>) => {
    const values = toArray(query[key])
    if (!values.length) return []
    if (!options?.shouldIgnoreQuery) return values
    return values.filter((value) => !options.shouldIgnoreQuery?.(key, value, query))
  }

  const hasAnyFilterInQuery = (query: Record<string, unknown>) =>
    filterKeys.some((k) => filteredQueryValues(k, query).length > 0)

  // query -> filters
  const applyFromQuery = (query: Record<string, unknown>) => {
    let changed = false
    const next = { ...filters.value }
    for (const key of filterKeys) {
      const valuesFromQuery = filteredQueryValues(key, query)
      const current = filters.value[key] || []
      const fromQuery = valuesFromQuery
      const same =
        current.length === fromQuery.length &&
        current.every((v, i) => v === fromQuery[i])
      if (!same) {
        next[key] = fromQuery as TFilters[keyof TFilters]
        changed = true
      }
    }
    if (changed) filters.value = next
  }

  // URL ändras (back/forward/manuell)
  watch(
    () => route.query,
    (q) => {
      if (guard.value) return
      const queryObj = q as Record<string, unknown>

      // Om URL saknar alla filter-nycklar, nollställ inte befintliga filter.
      // Låt istället filters->URL-watchern uppdatera URL:en.
      if (!hasAnyFilterInQuery(queryObj)) return

      applyFromQuery(queryObj)
    },
    { immediate: true }
  )

  // filters -> URL
  watch(
    filters,
    async () => {
      guard.value = true
      try {
        const other = Object.fromEntries(
          Object.entries(route.query).filter(([k]) => !filterKeys.includes(k as TKeys[number]))
        )
        const fromFilters: Record<string, string[] | undefined> = {}
        for (const key of filterKeys) {
          const arr = filters.value[key] || []
          fromFilters[key] = arr.length ? [...arr] : undefined // undefined tar bort nyckeln
        }
        await router.replace({ query: { ...other, ...fromFilters } })
      } finally {
        guard.value = false
      }
    },
    { deep: true }
  )
}
